import ListSubheader from '@mui/material/ListSubheader'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import autocompleteClasses from '@mui/material/Autocomplete/autocompleteClasses'
import Autocomplete from '@mui/material/Autocomplete'
/* eslint-disable @typescript-eslint/no-explicit-any */
import ErrorOverlay from 'Components/General/ErrorOverlay'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICertificate } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { chooseDBTranslation } from 'utils/translations'
import { collapseDuplicateOptions, fuseFiltering, normalizeOptionName } from 'utils/utils'
import { InputField } from '../InputField'
import { orderBy } from 'lodash'
import { certificateAPI } from 'api/CertificateAPI'
import { List, RowComponentProps, useListRef, ListImperativeAPI } from 'react-window'
import i18n from 'i18n'
import { useDidYouMean } from '../DidYouMeanDialog/useDidYouMean'

const LISTBOX_PADDING = 8

type ItemData = Array<
  | {
      key: number
      group: string
      children: React.ReactNode
    }
  | [React.ReactElement, string, number]
>

function RowComponent({ index, itemData, style }: RowComponentProps & { itemData: ItemData }) {
  const dataSet = itemData[index]
  const inlineStyle = {
    ...style,
    top: ((style.top as number) ?? 0) + LISTBOX_PADDING,
  }

  if ('group' in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    )
  }

  const { key, ...optionProps } = dataSet[0]

  return (
    <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
      {dataSet[0].key}
    </Typography>
  )
}

// Adapter for react-window v2
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & {
    internalListRef: React.Ref<ListImperativeAPI>
    onItemsBuilt: (optionIndexMap: Map<string, number>) => void
  }
>(function ListboxComponent(props, ref) {
  const { children, internalListRef, onItemsBuilt, ...other } = props
  const itemData: ItemData = []
  const optionIndexMap = React.useMemo(() => new Map<string, number>(), [])

  ;(children as ItemData).forEach(item => {
    itemData.push(item)
    if ('children' in item && Array.isArray(item.children)) {
      itemData.push(...item.children)
    }
  })

  // Map option values to their indices in the flattened array
  itemData.forEach((item, index) => {
    if (Array.isArray(item) && item[1]) {
      optionIndexMap.set(item[1], index)
    }
  })

  React.useEffect(() => {
    if (onItemsBuilt) {
      onItemsBuilt(optionIndexMap)
    }
  }, [onItemsBuilt, optionIndexMap])

  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child: ItemData[number]) => {
    if (Object.hasOwn(child, 'group')) {
      return 48
    }
    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  // Separate className for List, other props for wrapper div (ARIA, handlers)
  const { className, ...otherProps } = other

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef}
        key={itemCount}
        rowCount={itemCount}
        rowHeight={index => getChildSize(itemData[index])}
        rowComponent={RowComponent}
        rowProps={{ itemData }}
        style={{
          height: getHeight() + 2 * LISTBOX_PADDING,
          width: '100%',
        }}
        overscanCount={5}
        tagName="ul"
      />
    </div>
  )
})

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

type SingleValue = {
  multiple?: false
  value: ICertificate | null
  onChange: (newValue: ICertificate | null) => void
}

type MultiValue = {
  multiple: true
  value: ICertificate[]
  onChange: (newValue: ICertificate[]) => void
}

type Value = SingleValue | MultiValue

const getLabel = (item: ICertificate) => {
  const name = chooseDBTranslation(i18n, item).certificate
  const languages: string[] = []
  for (const currentItem of item.translations) {
    languages.push(currentItem.Language.name.toUpperCase())
  }
  return `${name} (${languages}) `
}

/**
 * Certificate picker component.
 *
 * @param label - The label text.
 * @param size - The size of the component.
 * @param required - Whether the field is required.
 * @param disabled - Whether the field is disabled.
 * @param name - The name of the field.
 * @param usedICertificates - The used skills.
 * @param placeholderLabel - The placeholder label text.
 * @param placeholderText - The placeholder text.
 * @param onKeyDown - The onKeyDown event handler.
 * @param adminView - Whether the field is for admin view.
 * @param icon - The icon of the field.
 * @param disableClearable - Whether the field is disableClearable.
 * @param search - Whether the field is searchable.
 * @param allowCreate - Whether a typed name that matches no option can be created; false only picks existing ones.
 * @returns The certificate picker component.
 * @notExported
 */
const CertificatePicker: React.FC<
  Value & {
    label?: string
    size?: 'small' | 'medium'
    required?: boolean
    disabled?: boolean
    certificate?: string
    usedCertificates?: ICertificate[]
    usedCertificateIds?: number[]
    placeholderLabel?: string
    placeholderText?: string
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
    adminView?: boolean
    icon?: React.ReactElement
    disableClearable?: boolean
    search?: boolean
    allowCreate?: boolean
    options?: ICertificate[]
    insetLabel?: boolean
    resetOnSelect?: boolean
    sx?: object
  }
> = ({
  multiple,
  size = 'small',
  value,
  onChange,
  label = 'Skill',
  required = false,
  disabled = false,
  certificate,
  usedCertificates = [],
  usedCertificateIds = [],
  placeholderLabel,
  placeholderText,
  onKeyDown,
  adminView = false,
  icon,
  disableClearable = false,
  search = false,
  allowCreate = true,
  options,
  insetLabel,
  resetOnSelect = false,
  sx,
}) => {
  const { t, i18n } = useTranslation()
  const [backendError, setBackendError] = useState<IError>()
  const [certificates, setCertificates] = useState<ICertificate[]>([])
  const [inputValue, setInputValue] = useState<string | undefined>(certificate ?? '')
  const [helperText, setHelperText] = useState<string>()
  const [componentValue, setComponentValue] = useState<Value['value']>(value ?? null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    if (options) {
      setCertificates(options)
      return
    }

    if (!options) {
      ;(async () => {
        try {
          let newCertificates: ICertificate[] = []
          newCertificates = await certificateAPI.getAll(controller)
          const orderedCertificates = orderBy(
            newCertificates,
            [cert => chooseDBTranslation(i18n, cert).certificate],
            ['asc']
          )
          setCertificates(orderedCertificates)
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [i18n, options])

  useEffect(() => {
    if (certificate) {
      setInputValue(certificate)
    }
  }, [certificate])

  useEffect(() => {
    if (!resetOnSelect) {
      if (multiple) {
        setInputValue('')
      }
      setComponentValue(value)
      if (value && !multiple) {
        setInputValue(chooseDBTranslation(i18n, value).certificate)
      }
    }
  }, [i18n, multiple, resetOnSelect, value])

  // Use react-window v2's useListRef hook for imperative API access
  const internalListRef = useListRef(null)
  const optionIndexMapRef = React.useRef<Map<string, number>>(new Map())

  const handleItemsBuilt = React.useCallback((optionIndexMap: Map<string, number>) => {
    optionIndexMapRef.current = optionIndexMap
  }, [])

  // Ids to prefer when they have duplicates, as a stable key so the options array (and the
  // fuseFiltering cache keyed by it) only changes when they do
  const keepIdsKey = [
    ...usedCertificates.map(item => item.id),
    ...usedCertificateIds,
    ...(Array.isArray(value) ? value : value ? [value] : []).map(item => item.id),
  ]
    .sort((a, b) => a - b)
    .join(',')

  // One option per name; admins see every duplicate (e.g. when merging)
  const shownCertificates = useMemo(
    () =>
      adminView
        ? certificates
        : collapseDuplicateOptions(
            certificates,
            certificate => chooseDBTranslation(i18n, certificate)?.certificate,
            keepIdsKey ? keepIdsKey.split(',').map(Number) : []
          ),
    [adminView, certificates, i18n, keepIdsKey]
  )

  const createCertificate = async (certificate: string) => {
    const newItem: ICertificate = await certificateAPI.createCertificate({
      certificate,
      language: i18n.language,
    })

    // The backend returns the existing certificate when one already has the same name apart from case or
    // punctuation, so it may already be in the list.
    if (!certificates.some(item => item.id === newItem.id)) {
      setCertificates([...certificates, newItem])
    }
    return newItem
  }

  // Used or already selected certificates can't be picked again, also from the "Did you mean" dialog
  const isCertificateDisabled = (certificate: ICertificate) =>
    usedCertificates.some(item => item.id === certificate.id) ||
    usedCertificateIds.includes(certificate.id) ||
    (Array.isArray(value) && value.some(item => item.id === certificate.id))

  const didYouMean = useDidYouMean({
    enabled: !adminView,
    options: shownCertificates,
    getNames: certificate => certificate.translations.map(translation => translation.certificate),
    getLabel: certificate => chooseDBTranslation(i18n, certificate).certificate,
    isDisabled: isCertificateDisabled,
  })

  // A name typed in the picker: an existing certificate picked from the "Did you mean" dialog, a new
  // certificate, or null when the user cancels or creating fails
  const resolveNewCertificate = async (name: string): Promise<ICertificate | null> => {
    const choice = await didYouMean.ask(name)
    if (!choice) return null
    if ('option' in choice) return choice.option

    setCreating(true)
    try {
      return await createCertificate(name)
    } catch (error) {
      setBackendError(error as IError)
      return null
    } finally {
      setCreating(false)
    }
  }
  const labelId = React.useId()

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <>
      {!placeholderLabel && !insetLabel && (
        <CaleoInputLabel id={labelId} icon={icon} label={label} required={required} />
      )}
      <Autocomplete
        data-testid="autocomplete-certificate-picker"
        selectOnFocus
        clearOnBlur
        disableClearable={disableClearable}
        blurOnSelect
        handleHomeEndKeys
        multiple={multiple}
        disabled={disabled || creating}
        inputValue={inputValue}
        value={(componentValue as typeof componentValue & string) ?? ''}
        onKeyDown={e => onKeyDown && onKeyDown(e)}
        options={shownCertificates as ((typeof certificates)[0] | string)[]}
        getOptionDisabled={option =>
          usedCertificates.some(cert => cert.id === (option as unknown as ICertificate)?.id) ||
          (typeof option !== 'string' && usedCertificateIds.includes(option.id))
        }
        onChange={async (_event, newValue) => {
          if (typeof newValue === 'string' || Array.isArray(newValue)) {
            let newCertificate: ICertificate[] | ICertificate
            if (Array.isArray(newValue)) {
              const items: ICertificate[] = []
              for (const certificate of newValue) {
                const item = typeof certificate === 'string' ? await resolveNewCertificate(certificate) : certificate
                if (item && !items.some(added => added.id === item.id)) {
                  items.push(item)
                }
              }
              newCertificate = items
            } else {
              const item = allowCreate ? await resolveNewCertificate(newValue) : null
              if (!item) return
              newCertificate = item
            }
            onChange(
              (multiple
                ? [...(newCertificate as ICertificate[])]
                : (newCertificate as ICertificate)) as unknown as ICertificate & ICertificate[]
            )
          } else {
            if (!newValue) {
              setInputValue('')
            } else {
              setInputValue(chooseDBTranslation(i18n, newValue).certificate)
            }
            onChange(newValue as unknown as ICertificate & ICertificate[])
          }
          setHelperText(undefined)
          if (resetOnSelect) {
            setComponentValue(null)
            setInputValue('')
          }
        }}
        filterOptions={(options, { inputValue }) => {
          const result = fuseFiltering(options, inputValue, ['translations.certificate'])

          if (allowCreate && inputValue && inputValue.length > 0) {
            const filteredCertificates = certificates.filter(
              certificate =>
                certificate &&
                normalizeOptionName(chooseDBTranslation(i18n, certificate).certificate) ===
                  normalizeOptionName(inputValue)
            )
            if (!filteredCertificates.length) result.push(inputValue)
          }

          return result
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') {
            return option
          }
          return adminView ? getLabel(option) : chooseDBTranslation(i18n, option).certificate
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof option === 'string' || typeof value === 'string') {
            return false
          }

          return option.id === value.id
        }}
        renderOption={(props, option, state) => {
          if (typeof option === 'string') {
            const newProps = { ...props, key: `${t(`placeholder.certificate`)} "${option}"` }
            return [newProps, `${t(`placeholder.certificate`)} "${option}"`, state.index] as React.ReactNode
          }

          return [props, option, state.index] as React.ReactNode
        }}
        renderInput={params => (
          <>
            {helperText && (
              <span
                style={{
                  fontSize: '0.75rem',
                  marginTop: 4,
                }}
              >
                {helperText}
              </span>
            )}
            <InputField
              slotProps={{
                inputLabel: insetLabel ? { shrink: true } : {},
                htmlInput: params?.inputProps,
                input: params?.InputProps,
              }}
              {...params}
              name="certificate"
              fullWidth
              placeholder={placeholderLabel ?? placeholderText}
              margin="dense"
              size={size}
              onChange={event => {
                if (
                  !search &&
                  typeof event.target.value === 'string' &&
                  (event.target.value.toLowerCase().includes(',') ||
                    event.target.value.toLowerCase().includes('/') ||
                    event.target.value.toLowerCase().includes(' ja ') ||
                    event.target.value.toLowerCase().includes(' and ') ||
                    event.target.value.toLowerCase().includes('\\'))
                ) {
                  setHelperText(t('certificateWarning', { type: t(`certificateLower`) }))
                } else {
                  setHelperText(undefined)
                }

                setInputValue(event.target.value)
              }}
              variant={placeholderLabel && !placeholderText ? 'standard' : 'outlined'}
              insetLabel={insetLabel}
              label={insetLabel ? label : undefined}
              ariaLabelledBy={!placeholderLabel && !insetLabel ? labelId : undefined}
              required={required}
            />
          </>
        )}
        renderGroup={params => params as unknown as React.ReactNode}
        sx={sx}
        slots={{
          popper: StyledPopper,
        }}
        slotProps={{
          listbox: {
            component: ListboxComponent,
            internalListRef,
            onItemsBuilt: handleItemsBuilt,
          } as any,
        }}
      />
      {didYouMean.dialog}
    </>
  )
}

export default CertificatePicker
